import { uuidLotus } from '@lotushealth/lotushealth_js_sdk';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function generateClientId(): string {
  return uuidLotus();
}

export function isLotusUuidV7(uuid: string): boolean {
  return decodeLotusUuidTimestampMs(uuid) !== null;
}

/**
 * Decode the unix timestamp generated by `uuidLotus()`.
 *
 * - first 36 bits: whole unix seconds
 * - next 24 bits: fractional second
 * - UUID version nibble: 7
 */
export function decodeLotusUuidTimestampMs(uuid: string): number | null {
  if (!UUID_REGEX.test(uuid)) {
    return null;
  }

  const compact = uuid.replaceAll('-', '');
  if (compact.length !== 32) {
    return null;
  }

  const versionNibble = compact[12];
  if (versionNibble === undefined || versionNibble.toLowerCase() !== '7') {
    return null;
  }

  const t1 = Number.parseInt(compact.slice(0, 8), 16);
  const t2 = Number.parseInt(compact.slice(8, 12), 16);
  const t3 = Number.parseInt(compact.slice(12, 16), 16);

  if (![t1, t2, t3].every(Number.isFinite)) {
    return null;
  }

  const seconds = t1 * 16 + Math.floor(t2 / 0x1000);
  const fractionalSecondBits = ((t2 & 0x0fff) << 12) | (t3 & 0x0fff);
  const fractionalMs = (fractionalSecondBits * 1000) / 2 ** 24;
  const timestampMs = seconds * 1000 + fractionalMs;

  return Number.isFinite(timestampMs) ? timestampMs : null;
}

export function decodeLotusUuidDate(uuid: string): Date | null {
  const timestampMs = decodeLotusUuidTimestampMs(uuid);
  if (timestampMs === null) {
    return null;
  }

  const date = new Date(timestampMs);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatLotusUuidDateTime(uuid: string): string | null {
  const date = decodeLotusUuidDate(uuid);
  if (date === null) {
    return null;
  }

  return date.toLocaleString([], {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function formatLotusUuidShortDate(uuid: string): string | null {
  const date = decodeLotusUuidDate(uuid);
  if (date === null) {
    return null;
  }

  return date.toLocaleString([], { month: 'short', day: 'numeric' });
}
